import { buddyClient, qs } from './client';

/**
 * nyce.buddy data access (buddy3 ADR-0020, `docs/design/data-access.md`): which NYCE.LOGIC groups may read which data
 * classes. Classes (`monetary`, `pii`, …) are marked on the data in buddy3's repo; profiles bundle the classes a group
 * may read; the per-customer work is the group → profile mapping. A user gets the union of their mapped groups'
 * profiles, `*` maps everyone else, nothing mapped = `full`. `secret` (passwords, licences) is nobody's.
 */

export type AccessScope = 'global' | 'tenant' | 'organization' | 'system';

export interface AccessProfile {
  key: string;
  name: string;
  description: string;
  /** The classes this profile may read; `*` = every known class. */
  classes: string[];
  /** `system` = shipped with buddy3, read-only. */
  origin: 'system' | 'custom';
  updatedAt?: string;
}

export interface AccessProfiles {
  /** The classes buddy3's entity model knows (never `secret`). */
  classes: string[];
  profiles: AccessProfile[];
}

export interface AccessProfileInput {
  name: string;
  description: string;
  classes: string[];
}

export interface AccessMapping {
  scope: AccessScope;
  scopeId: string;
  /** A NYCE.LOGIC user group as the token names it; `*` = everyone in no mapped group. */
  group: string;
  profile: string;
}

export interface AccessEffective {
  systemId: string;
  groups: string[];
  profiles: string[];
  matchedGroups: string[];
  /** No group of the user is mapped: the `*` row (or `full`) decided. */
  defaulted: boolean;
  note?: string | null;
  allowed: string[];
  denied: string[];
  knownClasses: string[];
  /** The rows the system's runtime resolved (most specific per group). */
  mapping: AccessMapping[];
  hiddenCount: number;
  /** `Entity.Requisite` the denied classes hide (first 200). */
  hidden: string[];
}

const seg = encodeURIComponent;

export function accessFor(target: string) {
  const c = buddyClient(target);
  return {
    profiles: () => c.get<AccessProfiles>('/access/profiles'),
    /** Create or update a custom profile (the shipped ones answer 403 `system_profile`). */
    putProfile: (key: string, body: AccessProfileInput) =>
      c.send<AccessProfile>('PUT', `/access/profiles/${seg(key)}`, body),
    deleteProfile: (key: string) => c.send<void>('DELETE', `/access/profiles/${seg(key)}`),
    mappings: (scope?: AccessScope, scopeId?: string) =>
      c.get<AccessMapping[]>(
        `/access/mappings${qs({ scope, scopeId: scope ? (scopeId ?? '') : undefined })}`
      ),
    /** Replaces every mapping of one scope. */
    putMappings: (scope: AccessScope, scopeId: string, mappings: { group: string; profile: string }[]) =>
      c.send<unknown>('PUT', '/access/mappings', {
        scope,
        scopeId: scope === 'global' ? '' : scopeId,
        mappings,
      }),
    /** "Test as": what a user in `groups` gets on one system. */
    effective: (systemId: string, groups: string[]) =>
      c.get<AccessEffective>(`/access/effective/${seg(systemId)}${qs({ groups: groups.join(',') })}`),
  };
}

export type AccessApi = ReturnType<typeof accessFor>;

/** `*` → every known class, for display. */
export const classesOf = (p: Pick<AccessProfile, 'classes'>, known: string[]) =>
  p.classes.includes('*') ? known : p.classes;
